import algoliasearch from 'algoliasearch/lite';
import clsx from 'clsx';
import Image from 'next/image';
import { NextSeo } from 'next-seo';
import React, { useState } from 'react';
import { useContext } from 'use-context-selector';
import { Breadcrumbs } from 'components/breadcrumbs/Breadcrumbs';
import { Shelf } from 'components/shelf/Shelf';
import { StaticPageContext } from 'components/staticPageContext/StaticPageProvider';
import type { FormattedContentfulSection } from 'utils/types/contentfulSections';
import { ContentSectionComponent } from '../contentSectionComponent/ContentSectionComponent';

import styles from './StaticPageContent.module.scss';

export const StaticPageContent = () => {
  const staticPageContext = useContext(StaticPageContext);
  if (staticPageContext === undefined) {
    throw new Error(
      'StaticContentContent must be used within a StaticPageContext'
    );
  }
  const { sections } = staticPageContext;
  const hasSections = sections && Boolean(sections?.length);
  const { seo, content, image, title } = staticPageContext;
  const [searchClient] = useState(() =>
    algoliasearch(
      process.env.NEXT_PUBLIC_ALGOLIA_ID ?? '',
      process.env.NEXT_PUBLIC_ALGOLIA_KEY ?? ''
    )
  );

  const isFullPageSection = (section: FormattedContentfulSection) => {
    return (
      section?.__typename === 'SliderHero' ||
      section?.__typename === 'CollectionListingSection' ||
      section?.__typename === 'ModuleServiceEducationListing' ||
      section?.__typename === 'FindYourSystem' ||
      section?.__typename === 'ColorSelector' ||
      section?.__typename === 'Shelf' ||
      section?.__typename === 'MainHero'
    );
  };

  return (
    <>
      <NextSeo
        title={seo?.title}
        description={seo?.description}
        noindex={seo?.noIndex}
      />

      <Breadcrumbs title={title} />
      {content && (
        <div className={clsx(styles.staticPageContent, 'container grid rte')}>
          <div className={image?.url ? 'col l2-5 xs4' : 'col xs4 l2-11'}>
            {content}
          </div>

          {image?.url && (
            <div
              className={clsx(
                styles['staticPageContent__img--column'],
                'col l7-11 xs4'
              )}
            >
              <Image
                src={image.url}
                alt={image.title}
                title={image.title}
                fill
                sizes="(max-width: 1024px) 100vw, 522px"
              />
            </div>
          )}
        </div>
      )}

      <div className="container">
        {hasSections &&
          sections.map((section, index) => {
            return (
              <div
                key={index}
                className={clsx({
                  [styles.staticPageFullWidthSection]:
                    isFullPageSection(section),
                })}
              >
                {section?.__typename === 'Shelf' ? (
                  <Shelf
                    franchiseBlock={section.franchiseBlock}
                    filters={section.filters}
                    inGridContent={section.inGridContent}
                    searchClient={searchClient}
                    fallbackCollection={section.fallbackCollection}
                    asIndex={false}
                  />
                ) : (
                  <ContentSectionComponent section={section} />
                )}
              </div>
            );
          })}
      </div>
    </>
  );
};
